import { GridCellParams, GridColDef, GridColumnHeaderParams, GridValidRowModel } from '@mui/x-data-grid-pro';
import IDCell from './Cells/IDCell/IDCell';
import ProjectDeptCell from './Cells/ProjectDeptCell/ProjectNameCell';
import CustomerCell from './Cells/CustomerCell/CustomerCell';
import AffairNameCell from './Cells/AffairNameCell/AffairNameCell';
import ConsultantCell from './Cells/ConsultantCell/ConsultantCell';
import AffairOtpCell from './Cells/AffairOtpCell/AffairOtpCell';
import OrderIdCell from './Cells/OrderIdCell/OrderIdCell';
import ForcastedDateCell from './Cells/ForcastedDateCell/ForcastedDateCell';
import DeliveryManagerCell from './Cells/DeliveryManagerCell/DeliveryManagerCell';
import BmCell from './Cells/BmCell/BmCell';
import RatingCell from './Cells/RatingCell/RatingCell';
import WorkunitNameCell from './Cells/WorkunitNameCell/WorkunitNameCell';
import WorkunitReferenceCell from './Cells/WorkunitReferenceCell/WorkunitReferenceCell';
import PriceCell from './Cells/PriceCell/PriceCell';
import WorkloadCell from './Cells/WorkloadCell/WorkloadCell';
import ScopeCell from './Cells/ScopeCell/ScopeCell';
import PendingAtCell from './Cells/PendingAtCell/PendingAtCell';
import OrderStatusCell from './Cells/OrderStatusCell/OrderStatusCell';
import DeliveryStatusCell from './Cells/DeliveryStatusCell/DeliveryStatusCell';
import ClientCell from './Cells/ClientCell/ClientCell';
import RefSpecCell from './Cells/RefSpecCell/RefSpecCell';
import NameSpecCell from './Cells/NameSpecCell/NameSpecCell';
import useUserRoles from 'hooks/useUserRoles';
import { find, forEach, map } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardKpiMetric from 'types/entities/DashboardKpiMetric';
import { KpiMetricTableType } from 'types/enums/KpiMetricTableType';
import OtpCell from './Cells/OtpCell/OtpCell';
import StartDateCell from './Cells/StartDateCell/StartDateCell';
import EndDateCell from './Cells/EndDateCell/EndDateCell';
import EDeliveryCell from './Cells/EDeliveryCell/EDeliveryCell';
import MonthYearCell from './Cells/MonthYearCell/MonthYearCell';
import PoReferenceCell from './Cells/PoReferenceCell/PoReferenceCell';
import TagCell from './Cells/TagCell/TagCell';
import Content from './Cells/ContentCell/ContentCell';

// type KpiWorkunitGridData = {
//   order?: { affair?: { name?: string } };
//    order?:
// };

// Grid rows come from an untyped API source (datas.map((row: any) => ...)) and each
// Cell component declares its own expected prop shape, so the row is intentionally `any`.
type KpiWorkunitGridCellParams<T> = GridCellParams<any, T, any>;

export const columnField = {
  PROJECT_DEPT: 'bu',
  CUSTOMER: 'customer',
  AFFAIR_NAME: 'affair_name',
  CONSULTANT: 'consultant_fullname',
  AFFAIROTP: 'affair_otp',
  ORDER_ID: 'order_id',
  FORECASTED_DATE: 'forecast_date',
  DELIVERY_MANAGER: 'delivery_manager_fullname',
  BM: 'bm_id',
  RATING: 'rating',
  WORKUNIT_NAME: 'workunit_name',
  WORKUNIT_REFERENCE: 'workunit_reference',
  CONTENT: 'content',
  PRICE: 'price',
  WORKLOAD: 'workload',
  SCOPE: 'scope_id',
  PENDINGAT: 'pendingat',
  ORDER_STATUS: 'status',
  DELIVERY_STATUS: 'mission_advancement_id',
  CLIENT: 'client_fullname',
  REF_SPEC: 'ref_spec',
  NAME_SPEC: 'name_spec',
  ID: 'id',
  OTP: 'otp',
  CUSTOMERCELL: 'name',
  ENDDATECELL: 'end_date',
  STARTDATECELL: 'start_date',
  EDELIVERY: 'e_delivery',
  BUSINESS_UNIT: 'bu',
  PO_REF: 'po_ref',
  MONTH_YEAR: 'month_year',
  TAG: 'tag',
} as const;

const defaultColumnParams: GridColDef = {
  field: '',
  headerName: '',
  align: 'left',
  headerAlign: 'center',
  filterable: false,
  description: '',
  flex: 1,
};

export const columnsParams: GridColDef[] = [
  {
    field: columnField.PROJECT_DEPT,
    headerName: 'BU',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <ProjectDeptCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.CUSTOMER,
    headerName: 'Customer',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <CustomerCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.NAME_SPEC,
    headerName: 'Project_Name',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <NameSpecCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.CONSULTANT,
    headerName: 'Consultant',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <ConsultantCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.AFFAIROTP,
    headerName: 'Affair_Otp',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <AffairOtpCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.ORDER_ID,
    headerName: 'Order_Number',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <OrderIdCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.FORECASTED_DATE,
    headerName: 'Delivery_Date',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <ForcastedDateCell {...params.row} />,
    minWidth: 100,
    sortable: false, // Sorting disabled
  },
  {
    field: columnField.DELIVERY_MANAGER,
    headerName: 'Delivery_Manager',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <DeliveryManagerCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.BM,
    headerName: 'Bm',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <BmCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.RATING,
    headerName: 'Kpi_Rating',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <RatingCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.WORKUNIT_NAME,
    headerName: 'Deliverable',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <WorkunitNameCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.WORKUNIT_REFERENCE,
    headerName: 'Reference',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <WorkunitReferenceCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.CONTENT,
    headerName: 'Content',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <Content {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.PRICE,
    headerName: 'Price',
    renderCell: (params: KpiWorkunitGridCellParams<number>) => <PriceCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.WORKLOAD,
    headerName: 'Charge',
    renderCell: (params: KpiWorkunitGridCellParams<['charge']>) => <WorkloadCell {...params.row} />,
    minWidth: 60,
    sortable: false, // Sorting disabled
  },
  {
    field: columnField.SCOPE,
    headerName: 'Scope',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <ScopeCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.ORDER_STATUS,
    headerName: 'Engagement_Model',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <OrderStatusCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.DELIVERY_STATUS,
    headerName: 'Kpi_Delivery_Status',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <DeliveryStatusCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.CLIENT,
    headerName: 'Client',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <ClientCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.REF_SPEC,
    headerName: 'Ref_Spec',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <RefSpecCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.AFFAIR_NAME,
    headerName: 'Engagement_Name',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <AffairNameCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.ID,
    headerName: 'ID',
    renderCell: (params: KpiWorkunitGridCellParams<number>) => <IDCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.OTP,
    headerName: 'OTP',
    renderCell: (params: KpiWorkunitGridCellParams<number>) => <OtpCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.CUSTOMERCELL,
    headerName: 'Customer',
    renderCell: (params: KpiWorkunitGridCellParams<number>) => <CustomerCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.STARTDATECELL,
    headerName: 'Start_Date',
    renderCell: (params: KpiWorkunitGridCellParams<number>) => <StartDateCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.ENDDATECELL,
    headerName: 'End_Date',
    renderCell: (params: KpiWorkunitGridCellParams<number>) => <EndDateCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.EDELIVERY,
    headerName: 'EDelivery',
    renderCell: (params: KpiWorkunitGridCellParams<number>) => <EDeliveryCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.PO_REF,
    headerName: 'Po_Ref',
    renderCell: (params: KpiWorkunitGridCellParams<number>) => <PoReferenceCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.MONTH_YEAR,
    headerName: 'Date',
    renderCell: (params: KpiWorkunitGridCellParams<number>) => <MonthYearCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.TAG,
    headerName: 'Tags',
    renderCell: (params: KpiWorkunitGridCellParams<number>) => <TagCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
];

type ColumnsFields = { [name: string]: string[] };

const commonColumns = [
  columnField.PROJECT_DEPT,
  columnField.CUSTOMER,
  columnField.NAME_SPEC,
  columnField.CONSULTANT,
  columnField.AFFAIROTP,
  columnField.ORDER_ID,
  columnField.FORECASTED_DATE,
  columnField.DELIVERY_MANAGER,
  columnField.BM,
  columnField.RATING,
  columnField.WORKUNIT_NAME,
  columnField.WORKUNIT_REFERENCE,
  columnField.CONTENT,
  columnField.PRICE,
  columnField.WORKLOAD,
  columnField.SCOPE,
  columnField.ORDER_STATUS,
  columnField.DELIVERY_STATUS,
  columnField.CLIENT,
  columnField.REF_SPEC,
  columnField.AFFAIR_NAME,
  columnField.ID,
];

const coverRatioColumns = [
  columnField.OTP,
  columnField.CUSTOMERCELL,
  columnField.STARTDATECELL,
  columnField.ENDDATECELL,
  columnField.EDELIVERY,
  columnField.CONSULTANT,
  //
];

const financialTableColumns = [
  columnField.PROJECT_DEPT,
  columnField.CUSTOMER,
  columnField.AFFAIROTP,
  columnField.BM,
  columnField.DELIVERY_MANAGER,
  columnField.CONSULTANT,
  columnField.PO_REF,
  columnField.PRICE,
  columnField.MONTH_YEAR,
  columnField.TAG,
  columnField.ORDER_ID,
  columnField.ID,
];

export const kpiMetricColumnsFields: ColumnsFields = {
  [KpiMetricTableType.WORKFLOW]: commonColumns,
  [KpiMetricTableType.ACCEPTANCE]: commonColumns,
  [KpiMetricTableType.PERFORMANCE]: commonColumns,
  [KpiMetricTableType.COVER_RATIO]: coverRatioColumns,
  [KpiMetricTableType.FINANCIAL_TABLE]: financialTableColumns,
  [KpiMetricTableType.PURCHASE_ORDER]: financialTableColumns,
};

/**
 * React hook to build columns variable for Mui Datagrid with specific components
 */
export const useColumns = (columnsFields: ColumnsFields, order_type_id: number) => {
  const [t] = useTranslation();
  const userRoles = useUserRoles();
  // const state = useSelector((state) => state.deliverable);
  return useMemo(() => {
    const columns: { [name: string]: GridColDef<DashboardKpiMetric, any, any>[] } = {};

    forEach(columnsFields, (fields, name) => {
      // Filter out the CONSULTANT field if the user is a client
      const filteredFields = userRoles.isClient
        ? fields.filter((field) => field !== columnField.CONSULTANT && field !== columnField.WORKLOAD)
        : fields;

      columns[name] = map(filteredFields, (field) => {
        const columnParam = find(columnsParams, ['field', field]);
        return {
          ...defaultColumnParams,
          ...columnParam,
          ...{ headerName: columnParam?.headerName ? t(columnParam.headerName) : '' },
        };
      });
    });
    return columns;
  }, [columnsFields, userRoles, t]);
};
